/**
 * Text toolbar component.
 *
 * Provides text size controls and highlighter toggle. Rendered inline
 * (typically inside the reading panel header's `rightSlot`) so it sits
 * alongside the section title instead of floating on top of it.
 *
 * @module TextToolbar
 */

import { Highlighter, Eraser, Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface TextToolbarProps {
  readonly textSize: 'small' | 'medium' | 'large';
  readonly highlighterActive: boolean;
  readonly onTextSizeChange: (size: 'small' | 'medium' | 'large') => void;
  readonly onHighlighterToggle: () => void;
  readonly onEraseHighlights: () => void;
}

export function TextToolbar({
  textSize,
  highlighterActive,
  onTextSizeChange,
  onHighlighterToggle,
  onEraseHighlights,
}: TextToolbarProps) {
  const handleSizeDecrease = () => {
    if (textSize === 'large') onTextSizeChange('medium');
    else if (textSize === 'medium') onTextSizeChange('small');
  };

  const handleSizeIncrease = () => {
    if (textSize === 'small') onTextSizeChange('medium');
    else if (textSize === 'medium') onTextSizeChange('large');
  };

  return (
    <div className="inline-flex items-center gap-0.5 sm:gap-1 bg-card/90 border border-border rounded-lg p-1 sm:p-1.5 shadow-sm transition-all">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 w-8 p-0 rounded-md transition-all",
              highlighterActive && "bg-primary/10"
            )}
            onClick={onHighlighterToggle}
          >
            <Highlighter className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Highlighter {highlighterActive ? '(On)' : '(Off)'}</p>
        </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 rounded-md"
            onClick={onEraseHighlights}
          >
            <Eraser className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Clear highlights</p>
        </TooltipContent>
      </Tooltip>

      <div className="w-px h-6 bg-border mx-1" />

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 rounded-md"
            onClick={handleSizeDecrease}
            disabled={textSize === 'small'}
          >
            <Minus className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Decrease text size</p>
        </TooltipContent>
      </Tooltip>

      <span className="text-xs font-medium text-muted-foreground px-1 min-w-[24px] text-center">
        A<span
          className={cn(
            textSize === 'small' && 'text-[10px]',
            textSize === 'medium' && 'text-xs',
            textSize === 'large' && 'text-sm'
          )}
        >
          {(() => {
            if (textSize === 'small') return '-';
            if (textSize === 'large') return '+';
            return '';
          })()}
        </span>
      </span>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 rounded-md"
            onClick={handleSizeIncrease}
            disabled={textSize === 'large'}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Increase text size</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

